'use client'

import clsx from 'clsx'
import { useParams } from 'next/navigation'
import { useEffect } from 'react'

import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import { range } from '@/utils/array'
import {
  trackTypeLocalSearch,
  trackViewDataIndukProvince,
} from '@/utils/tracker/modules/datainduk'
import { TrackerSection, TrackerSubSection } from '@/utils/tracker/shared'
import {
  useFetchYayasanStatsByParentWilayah,
  useWilayahBreadcrumbs,
} from '@/apis/useDataIndukYayasan'

import { Container } from '@/components/Container'
import { EmptyState } from '@/components/EmptyState'
import { LinkWithTracker } from '@/components/LinkWithTracker'

import { DATA_INDUK_YAYASAN_ROUTE } from '@/configs/constants'

import { AreaCard } from '../components/AreaCard/AreaCard'
import { AreaCardLoader } from '../components/AreaCard/AreaCardLoader'
import { AreaSummaryStatsYayasan } from '../components/AreaSummaryStats/AreaSummaryStatsYayasan'
import { DataIndukSearchBox } from '../components/DataIndukSearchBox'
import { KabKotChips } from '../components/KabKotChips'
import { LastUpdatedAt } from '../components/LastUpdatedAt'
import { LocalSearch } from '../components/LocalSearch'
import { NavSection } from '../components/NavSection'
import { ListOfProvinces } from '../components/ProvinceLogoCard/ListOfProvinces'
import { TitlePage } from '../components/TitlePage'
import { DATA_INDUK_TITLE_PREFIX, VARIANT } from '../constant'
import {
  getLatestWilayahName,
  getLogoProvinceLink,
  injectImageProvince,
  sumTotalStatsYayasan,
} from '../helpers'
import { useFilterState } from '../hooks/useFilterState'
import { useKabKotSwicher } from '../hooks/useKabKotSwicher'
import { KODE_WILAYAH_LUAR_NEGERI } from '../provinces'

export const YayasanProvinsi = () => {
  const params = useParams()
  const provinceId = (params?.provinsi_id as string) || ''

  const { getFilterSearchParamsFromQuery } = useFilterState({
    variant: VARIANT.YAYASAN,
    view: 'list',
  })

  // eslint-disable-next-line unused-imports/no-unused-vars
  const isLuarNegeri = provinceId === KODE_WILAYAH_LUAR_NEGERI

  const {
    data: dataWilayahAncestors,
    isPending: isPendingWilayahAncestors,
    error: errorWilayahAncestors,
  } = useWilayahBreadcrumbs(provinceId, {
    enabled: !!provinceId,
  })

  // eslint-disable-next-line unused-imports/no-unused-vars
  const { data: dataStatsByParent, isPending: isPendingStatsByParent } =
    useFetchYayasanStatsByParentWilayah(provinceId, {
      enabled: !!provinceId,
    })

  const { activeChips, filteredData, setActiveChips, onSearchByWilayah } =
    useKabKotSwicher({
      data: dataStatsByParent,
      isPending: isPendingStatsByParent,
    })

  const handleSubmitLocalSearch = (keywordLocalSearch: string) => {
    if (keywordLocalSearch !== '') {
      trackTypeLocalSearch({
        keyword: keywordLocalSearch,
        locationLevel: 'Provinsi',
        locationId: provinceId,
        section: TrackerSection.YAYASAN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
    }
    onSearchByWilayah(keywordLocalSearch)
  }

  useEffect(() => {
    if (provinceId) {
      trackViewDataIndukProvince({
        provinceId,
        section: TrackerSection.YAYASAN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
    }
  }, [provinceId])

  return (
    <>
      <section className="flex flex-col" id="search-section">
        <DataIndukSearchBox
          source={`${DATA_INDUK_YAYASAN_ROUTE}/${provinceId}`}
          locationId={`${provinceId}`}
          locationLevel="Provinsi"
          placeholder="Cari Data Yayasan"
          variant="yayasan"
        />
      </section>

      {!isPendingWilayahAncestors && !isLuarNegeri && (
        <NavSection
          isPending={isPendingWilayahAncestors}
          data={dataWilayahAncestors}
          level={1}
          withRoot={true}
          prefixPath={DATA_INDUK_YAYASAN_ROUTE}
          className="pt-4"
        />
      )}

      <section
        className={clsx('py-4 ', isLuarNegeri ? 'bg-white' : 'bg-gray-5')}
        id="table-section"
      >
        <Container>
          {isLuarNegeri ? (
            <div>
              <LinkWithTracker
                className="flex items-center gap-2"
                href={DATA_INDUK_YAYASAN_ROUTE}
              >
                <Icon>arrow_back</Icon>
                <Text className="hover:underline">Kembali</Text>
              </LinkWithTracker>
            </div>
          ) : null}
          {!isPendingWilayahAncestors && errorWilayahAncestors ? (
            <div className="mb-4 min-h-[400px] w-full rounded-lg bg-white p-6">
              <EmptyState
                title="Data tidak ditemukan"
                description="Maaf data tidak dapat ditemukan, silahkan pilih data lain"
                size="sm"
              />
            </div>
          ) : (
            <>
              <div
                className={clsx(
                  'min-h-[500px] w-full rounded-lg bg-white',
                  isLuarNegeri ? 'pt-0' : 'p-6'
                )}
              >
                <div className={clsx(isLuarNegeri ? 'pb-4 pt-6' : '')}>
                  <AreaSummaryStatsYayasan
                    withImage={true}
                    isPending={isPendingStatsByParent}
                    total={
                      !isPendingStatsByParent && dataStatsByParent
                        ? sumTotalStatsYayasan(dataStatsByParent?.data)
                        : 0
                    }
                    title={`${DATA_INDUK_TITLE_PREFIX[VARIANT.YAYASAN]} di ${getLatestWilayahName({ data: dataWilayahAncestors })}`}
                    image={`${getLogoProvinceLink({
                      data: dataWilayahAncestors,
                      withDefault: true,
                    })}`}
                    level={'district'}
                    wrapperClass="mb-4"
                  />

                  <TitlePage variant={VARIANT.YAYASAN} />
                </div>

                {!isLuarNegeri && (
                  <div className="mb-4 flex w-full flex-col lg:flex-row lg:items-center lg:justify-between">
                    <KabKotChips
                      activeChips={activeChips}
                      onClick={(newActive) => {
                        if (activeChips.includes(newActive)) {
                          setActiveChips((prevState) => {
                            return prevState.filter(
                              (item) => item !== newActive
                            )
                          })
                        } else {
                          setActiveChips((prevState) => {
                            return [...prevState, newActive]
                          })
                        }
                      }}
                    />

                    <LocalSearch onSubmit={handleSubmitLocalSearch} />
                  </div>
                )}

                {isLuarNegeri ? (
                  <ListOfProvinces
                    provinceId={provinceId}
                    isPending={isPendingStatsByParent}
                    provinces={injectImageProvince(filteredData || [])}
                    additionalParams={getFilterSearchParamsFromQuery()}
                    prefixPath={DATA_INDUK_YAYASAN_ROUTE}
                    variant="yayasan"
                  />
                ) : (
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                    {isPendingStatsByParent ? (
                      <>
                        {range(1, 5).map((item) => (
                          <AreaCardLoader key={item} />
                        ))}
                      </>
                    ) : (
                      <>
                        {filteredData.map((item) => {
                          return (
                            <AreaCard
                              key={item.district.kodeWilayah}
                              code={item.district.kodeWilayah}
                              title={item.district.namaWilayah}
                              link={`${DATA_INDUK_YAYASAN_ROUTE}/${provinceId}/${item.district.kodeWilayah}`}
                              additionalParams={getFilterSearchParamsFromQuery()}
                              textLink={`Lihat Per ${
                                isLuarNegeri ? 'Kota' : 'Kab/Kota'
                              }`}
                              stats={item.yayasanPendidikanStatistics}
                              variant="yayasan"
                            />
                          )
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>

              {dataStatsByParent?.meta?.lastUpdatedAt && (
                <LastUpdatedAt value={dataStatsByParent?.meta.lastUpdatedAt} />
              )}
            </>
          )}
        </Container>
      </section>
    </>
  )
}
